import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import type { AnalysisTable, SpecialistOutput } from '@/api/smartMarketplace';

export type AnalysisSection = {
  agentName: string;
  tables: AnalysisTable[];
};

export function cellText(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

export function filledTables(tables: AnalysisTable[] | undefined): AnalysisTable[] {
  return (tables ?? []).filter((table) => table.title.trim() !== '' && table.columns.length > 0);
}

/** Pad a short row so every displayed cell lines up with a column. */
export function displayRows(table: AnalysisTable): string[][] {
  return table.rows.map((row) => table.columns.map((_, index) => cellText(row[index])));
}

export function analysisSections(outputs: SpecialistOutput[]): AnalysisSection[] {
  return outputs
    .map((output) => ({
      agentName: output.agent_name,
      tables: filledTables(output.tables),
    }))
    .filter((section) => section.tables.length > 0);
}

export function AnalysisTableView({ table }: { table: AnalysisTable }) {
  const rows = displayRows(table);
  return (
    <div className="space-y-1">
      <h4 className="text-body-sm font-medium text-brand-ink">{table.title}</h4>
      <TableWrap compact>
        <Table compact>
          <TableHeader>
            <TableRow>
              {table.columns.map((column, index) => (
                <TableHead key={`${column}-${index}`}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <tbody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.columns.length}>None</TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={`${table.title}-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <TableCell key={`${table.title}-${rowIndex}-${cellIndex}`}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </tbody>
        </Table>
      </TableWrap>
      {table.note?.trim() ? <p className="text-body-sm text-foreground-muted">{table.note}</p> : null}
    </div>
  );
}

export function AnalysisTableList({ tables }: { tables: AnalysisTable[] | undefined }) {
  const filled = filledTables(tables);
  if (filled.length === 0) return null;
  return (
    <div className="space-y-3">
      {filled.map((table, index) => (
        <AnalysisTableView key={`${table.title}-${index}`} table={table} />
      ))}
    </div>
  );
}
